import { GUARD_LABEL } from '@/features/fee-runs/fee-run-helpers';
import { getErrorMessage, HTTP_UNPROCESSABLE_ENTITY, isForbiddenError, isHttpStatus } from '@/lib/api/error';
import type { FeeRunEnqueueIncompleteBody, FeeRunGuardsFailedBody } from '@/services/api/fee-runs';

export type ApproveErrorKind = 'forbidden' | 'guards-failed' | 'enqueue-incomplete' | 'other';

export interface ApproveError {
  kind: ApproveErrorKind;
  message: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isGuardsFailedBody = (body: unknown): body is FeeRunGuardsFailedBody => isRecord(body) && body.error === 'GUARDS_FAILED' && Array.isArray(body.guards);

const isEnqueueIncompleteBody = (body: unknown): body is FeeRunEnqueueIncompleteBody =>
  isRecord(body) && body.error === 'ENQUEUE_INCOMPLETE' && typeof body.sent === 'number' && typeof body.failed === 'number';

/**
 * The approval failure as the operator should read it. `enqueue-incomplete` is not a failure of the approval: the run
 * is collecting, but some account holders never reached the queue and need a Retry.
 */
export const parseApproveError = async (error: unknown): Promise<ApproveError> => {
  if (isForbiddenError(error)) return { kind: 'forbidden', message: 'You are not authorised to approve fee runs.' };
  if (isHttpStatus(error, HTTP_UNPROCESSABLE_ENTITY)) {
    let body: unknown;
    try {
      body = await error.response.clone().json();
    } catch {
      body = undefined;
    }
    if (isGuardsFailedBody(body)) {
      const failing = body.guards.filter((guard) => !guard.passed).map((guard) => GUARD_LABEL[guard.name]?.clause ?? guard.name);
      return { kind: 'guards-failed', message: `Approval blocked: ${failing.join('; ')}.` };
    }
    if (isEnqueueIncompleteBody(body)) {
      return {
        kind: 'enqueue-incomplete',
        message: `Collection started, but ${body.failed} of ${body.sent + body.failed} account holders could not be queued. Use Retry to queue them.`,
      };
    }
  }
  return { kind: 'other', message: getErrorMessage(error) };
};

export const approveErrorMessage = async (error: unknown): Promise<string> => (await parseApproveError(error)).message;
